import React from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Baby } from "lucide-react";

export interface ChildEntry {
  id: string;
  name: string;
  geburtsdatum: string;
  svNummer?: string;
  produkte?: Record<string, boolean>;
}

interface ChildrenDataTableProps {
  value: ChildEntry[];
  onChange: (value: ChildEntry[]) => void;
  produktOptionen?: string[];
  maxChildren?: number;
  showSvNummer?: boolean;
  className?: string;
}

const createEmptyChild = (): ChildEntry => ({
  id: crypto.randomUUID(),
  name: "",
  geburtsdatum: "",
  svNummer: "",
  produkte: {},
});

export function ChildrenDataTable({
  value = [],
  onChange,
  produktOptionen = ["Sparbuch", "Bausparen", "Fondspolizze", "Unfallversicherung"],
  maxChildren = 10,
  showSvNummer = true,
  className,
}: ChildrenDataTableProps) {
  const addChild = () => {
    if (value.length < maxChildren) {
      onChange([...value, createEmptyChild()]);
    }
  };

  const removeChild = (index: number) => {
    const updated = [...value];
    updated.splice(index, 1);
    onChange(updated);
  };

  const updateChild = (index: number, field: keyof ChildEntry, fieldValue: any) => {
    const updated = [...value];
    updated[index] = { ...updated[index], [field]: fieldValue };
    onChange(updated);
  };

  const toggleProdukt = (childIndex: number, produkt: string) => {
    const child = value[childIndex];
    const produkte = { ...child.produkte };
    produkte[produkt] = !produkte[produkt];
    updateChild(childIndex, "produkte", produkte);
  };

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Baby className="h-4 w-4" />
          Kinderdaten
        </Label>
        {value.length < maxChildren && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChild}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Kind hinzufügen
          </Button>
        )}
      </div>

      {value.length === 0 ? (
        <div className="text-center py-8 bg-muted/30 rounded-lg border-2 border-dashed">
          <Baby className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            Noch keine Kinder erfasst
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChild}
            className="mt-3"
          >
            Erstes Kind hinzufügen
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {value.map((child, index) => (
            <div
              key={child.id}
              className="p-4 bg-muted/30 rounded-lg border space-y-4"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Kind {index + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeChild(index)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={child.name}
                    onChange={(e) => updateChild(index, "name", e.target.value)}
                    placeholder="Vorname Nachname"
                    className="h-9"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs">Geburtsdatum</Label>
                  <Input
                    type="date"
                    value={child.geburtsdatum}
                    onChange={(e) => updateChild(index, "geburtsdatum", e.target.value)}
                    className="h-9"
                  />
                </div>
                
                {showSvNummer && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">SV-Nummer</Label>
                    <Input
                      value={child.svNummer || ""}
                      onChange={(e) => updateChild(index, "svNummer", e.target.value)}
                      placeholder="1234 TTMMJJ"
                      className="h-9"
                    />
                  </div>
                )}
              </div>

              {produktOptionen.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Vorsorgeprodukte</Label>
                  <div className="flex flex-wrap gap-3">
                    {produktOptionen.map((produkt) => (
                      <label
                        key={produkt}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Checkbox
                          checked={child.produkte?.[produkt] || false}
                          onCheckedChange={() => toggleProdukt(index, produkt)}
                        />
                        <span className="text-sm">{produkt}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
